import { FC, useEffect, useRef, useState } from 'react';
import styles from './index.module.less';
import * as echarts from 'echarts/core';
import { unstable_batchedUpdates } from 'react-dom';
import { throttle } from '@lhb/func';
import { GridComponent, TooltipComponent, TitleComponent, LegendComponent, DataZoomComponent, MarkLineComponent } from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import { LineChart } from 'echarts/charts';
import { LabelLayout, UniversalTransition } from 'echarts/features';

echarts.use([
  SVGRenderer,
  LineChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  DataZoomComponent,
  LabelLayout,
  UniversalTransition,
  MarkLineComponent
]);

/**
 * @Description 点位客流折线图
 */
const SpotFlowChart:FC<{
  width,
  height,
  xAxisData,
  seriesData,
  resizable?: boolean,
}> = ({
  width,
  height,
  xAxisData,
  seriesData,
  resizable = false
}) => {
  const chartRef = useRef<any>(null);
  const [echartsInstance, setEchartsInstance] = useState<any>(null);

  const [isConfig, setIsConfig] = useState(false); // options 是否配置完成
  const [options, setOptions] = useState<any>({
    title: {
      show: false,
    },
    legend: {
      show: false,
    },
    color: '#FFBB56',
    yAxis: {
      show: false,
      min: (value) => {
        if (value.min === value.max) {
          return value.min - 10;
        }
        return value.min;
      },
      max: (value) => {
        if (value.min === value.max) {
          return value.max + 10;
        }
        return value.max;
      }
    },
    xAxis: {
      show: false,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: (params) => {
        const item = params?.[0] || {};
        return `<div class="c-666">${item?.name}</div><div class="c-222 bold">${item.value}</div>`;
      },
    },
    grid: {
      top: 0,
      bottom: 0,
      right: 0,
      left: 0
    }
  });

  useEffect(() => {
    if (xAxisData?.length && seriesData?.length) {
      unstable_batchedUpdates(() => {
        setOptions({
          ...options,
          series: seriesData.map((item) => {
            return {
              type: 'line',
              symbol: 'none',
              label: {
                show: false
              },
              zlevel: 10,
              lineStyle: {
                width: 1
              },
              markLine: {
                symbol: 'none',
                label: {
                  show: false,
                },
                data: [
                  [{
                    x: '0%',
                    y: '50%'
                  },
                  {
                    x: '100%',
                    y: '50%'
                  }]
                ],
                lineStyle: {
                  color: '#E8E8E9'
                }
              },
              ...item
            };
          }),
          xAxis: {
            ...options.xAxis,
            data: xAxisData,
            boundaryGap: false,
            axisTick: {
              alignWithLabel: true
            }
          },
          yAxis: {
            ...options.yAxis,
          },
        });
        setIsConfig(true);
      });
    }
  }, [xAxisData, seriesData]);

  useEffect(() => {
    if (!isConfig) return;
    let chart: any;
    if (chartRef.current) {
      chart = echarts.init(chartRef.current, 'light', {
        renderer: 'svg',
        height,
      });
      chart.setOption(options);
      setEchartsInstance(chart);
    }
    return () => chart?.dispose();
  }, [options, isConfig]);

  useEffect(() => {
    if (echartsInstance && resizable) {
      window.addEventListener('resize', throttle(() => {
        echartsInstance.resize(); // 图表根据窗口大小自适应
      }, 300));
    }
    return () => {
      if (resizable) {
        window.removeEventListener('resize', throttle(() => {
          echartsInstance.resize();
        }, 300));
      }
    };
  }, [echartsInstance, resizable]);

  return (
    <div className={styles.spotChartWrapper} style={{ width, height }}>
      <div ref={chartRef} className={styles.spotChart}></div>
    </div>
  );
};

export default SpotFlowChart;
